<template>
    <div class="flex h-screen min-h-0 w-full flex-col bg-panel">
        <!-- Mobile/tablet top bar: opens the board drawer; detail uses the full width underneath. -->
        <div class="flex items-center gap-3 border-b border-panel-border bg-white px-4 py-2 lg:hidden">
            <button
                type="button"
                class="flex items-center gap-2 !rounded-lg border border-panel-border bg-panel px-3 py-1.5 text-sm font-semibold text-brand-header shadow-sm transition active:bg-board-card-hover"
                data-testid="board-toggle"
                @click="railOpen = true"
            >
                <icon icon="cil-list" />
                {{ trans('external.board.toggle', { count: totalCount }) }}
            </button>
        </div>

        <div class="flex min-h-0 flex-1 overflow-hidden">
            <!-- Board rail: persistent column on lg+, off-canvas drawer below. -->
            <aside
                :class="railOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'"
                class="fixed inset-y-0 left-0 z-40 flex min-h-0 w-[88vw] max-w-[360px] flex-col border-r border-panel-border bg-panel transition-transform duration-200 lg:static lg:z-auto lg:w-[340px] lg:max-w-none lg:shrink-0 xl:w-[380px]"
                data-testid="external-board-rail"
            >
                <Deferred data="boardItems">
                    <template #fallback>
                        <div class="flex items-center gap-2 border-b border-panel-border bg-panel px-4 py-3">
                            <h2 class="text-sm font-bold tracking-wide text-brand-header uppercase">{{ trans('external.board.title') }}</h2>
                        </div>
                        <div class="min-h-0 flex-1 overflow-y-auto px-3 py-3">
                            <external-work-order-rail-skeleton />
                        </div>
                    </template>

                    <external-work-order-rail
                        :groups="groups"
                        :selected-id="selectedWorkOrderId"
                        :dismissible="true"
                        @select="selectWorkOrder"
                        @dismiss="railOpen = false"
                    />
                </Deferred>
            </aside>

            <!-- Scrim for the drawer on small screens. -->
            <div v-if="railOpen" class="fixed inset-0 z-30 bg-black/30 lg:hidden" @click="railOpen = false"></div>

            <!-- Work order detail. -->
            <main class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
                <div class="min-h-0 flex-1 overflow-y-auto">
                    <work-order-detail
                        v-if="workOrder"
                        :key="workOrder.id"
                        :embedded="true"
                        :show-footer="false"
                        close-mode="none"
                        :external="external"
                        :external-tab-token="externalTabToken"
                        :work-order="workOrder"
                        :work-order-history="workOrderHistory"
                        :statuses="statuses"
                        :priorities="priorities"
                        :assignees="assignees"
                        :is-editable="isEditable"
                        :can-reassign="canReassign"
                        :can-update-asset-operability="canUpdateAssetOperability"
                        :work-order-property-config="workOrderPropertyConfig"
                        :impact-assessment="impactAssessment"
                        :catsa-extras="catsaExtras"
                        :msd-notification="msdNotification"
                        :default-contact-phone="defaultContactPhone"
                        :case-categories="caseCategories"
                        :inoperable-reminder-key="inoperableReminderKey"
                    />
                    <external-board-empty-state v-else :message="trans('external.board.select_hint')" />
                </div>
                <the-footer class="shrink-0 border-t border-panel-border px-6 py-3" />
            </main>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { Deferred, router, usePage } from '@inertiajs/vue3';
import { eventBus, events } from '@/eventBus.js';
import WorkOrderDetail from '@/Pages/WorkOrders/WorkOrderDetail.vue';
import ExternalWorkOrderRail from '@/Pages/WorkOrders/ExternalWorkOrderRail.vue';
import ExternalWorkOrderRailSkeleton from '@/Pages/WorkOrders/ExternalWorkOrderRailSkeleton.vue';
import ExternalBoardEmptyState from '@/Pages/WorkOrders/ExternalBoardEmptyState.vue';
import TheFooter from '@/Containers/TheFooter.vue';
import { trans } from '@/mixins/i18n.js';

const props = defineProps({
    token: {
        type: String,
        required: true,
    },
    external: Boolean,
    selectedWorkOrderId: {
        type: [String, Number, null],
        default: null,
    },
    boardItems: {
        type: [Array, null],
        default: null,
    },
    // Server-translated lane headers: [{ value, label }] for to_do / in_progress / done.
    lanes: {
        type: Array,
        default: () => [],
    },
    // Detail props (forwarded to WorkOrderDetail; null when no work order is selected).
    workOrder: {
        type: [Object, null],
        default: null,
    },
    workOrderHistory: { type: [Array, null], default: null },
    statuses: { type: [Object, null], default: null },
    priorities: { type: [Object, null], default: null },
    assignees: { type: [Array, null], default: null },
    isEditable: { type: Boolean, default: false },
    canReassign: { type: Boolean, default: true },
    canUpdateAssetOperability: { type: Boolean, default: false },
    workOrderPropertyConfig: { type: [Object, null], default: null },
    impactAssessment: { type: Object, default: null },
    catsaExtras: { type: Object, default: null },
    msdNotification: { type: Object, default: null },
    defaultContactPhone: { type: [String, null], default: null },
    caseCategories: { type: Array, default: () => [] },
    inoperableReminderKey: { type: [String, null], default: null },
});

const page = usePage();
const railOpen = ref(false);

const externalTabToken = computed(() => page.props.externalTabToken ?? null);

// draft folds into the to_do lane; lane headers come server-translated via the `lanes` prop.
const LANE_STATUSES = {
    to_do: ['draft', 'to_do'],
    in_progress: ['in_progress'],
    done: ['done'],
};

const groups = computed(() =>
    (props.lanes ?? []).map((lane) => ({
        key: lane.value,
        label: lane.label,
        items: (props.boardItems ?? []).filter((workOrder) => (LANE_STATUSES[lane.value] ?? [lane.value]).includes(workOrder.status?.value)),
    })),
);

const totalCount = computed(() => (props.boardItems ?? []).length);

function selectWorkOrder(id) {
    railOpen.value = false;

    if (id === props.selectedWorkOrderId) {
        return;
    }

    router.visit(`/external/${props.token}/workorders/${id}`, {
        only: [
            'workOrder',
            'workOrderHistory',
            'statuses',
            'priorities',
            'assignees',
            'isEditable',
            'canReassign',
            'canUpdateAssetOperability',
            'workOrderPropertyConfig',
            'inoperableReminderKey',
            'impactAssessment',
            'catsaExtras',
            'msdNotification',
            'caseCategories',
            'defaultContactPhone',
            'selectedWorkOrderId',
            // Re-fetch (clear) the one-time flash so the post-save toast doesn't replay when the
            // keyed WorkOrderDetail — and its nested FlashMessage — remounts on switching work orders.
            'flash',
        ],
        preserveState: true,
        preserveScroll: true,
        // Replace history so Back exits the board instead of stepping through every viewed card.
        replace: true,
    });
}

// An edit in the detail pane (WorkOrderDetail emits workOrderUpdated on every save) can move the card
// to another lane or change what it shows. Reload just the board items so the rail re-sorts in place,
// without re-fetching the detail or flashing the skeleton.
function refreshBoard() {
    router.reload({ only: ['boardItems'], preserveScroll: true });
}

onMounted(() => {
    eventBus.$on(events.workOrderUpdated, refreshBoard);
});

onUnmounted(() => {
    eventBus.$off(events.workOrderUpdated, refreshBoard);
});
</script>
